/**
 * PacketFlow — the Packet tab sub-flow, extracted from FoodScanner (Session 3
 * file split). Owns the label OCR lifecycle: quality pre-check → capture →
 * result states → multi-photo merge. Barcode lookup stays with the parent
 * (it renders into the meal result view).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { isGuest, useGuestStore } from "../../store/guestStore";
import { checkImageQuality } from "../../utils/imageQuality";
import { fireScanEvent } from "../../utils/compareFacts";
import BarcodeScanner from "./BarcodeScanner";
import CompareView from "./CompareView";
import LabelResults from "./LabelResults";

export default function PacketFlow({
  active,
  labelMode,
  barcodeBusy,
  externalCapture,
  onExternalConsumed,
  onBarcode,
  onWrongMode,
  onRequireSignupGate,
  onRequireReg,
  onOffline,
  trackGuest,
  refreshRecent,
}) {
  const user = useAuthStore((s) => s.user);
  const guestStore = useGuestStore();
  const guest = !user && isGuest();

  const [labelBusy, setLabelBusy] = useState(false);
  const [labelResult, setLabelResult] = useState(null);
  const [labelError, setLabelError] = useState(null);
  const [labelImage, setLabelImage] = useState(null);
  const [labelNotice, setLabelNotice] = useState(null);
  const [mergePrior, setMergePrior] = useState(null);
  const [mergeKind, setMergeKind] = useState(null); // 'ingredients' | 'panel'
  const [manualMode, setManualMode] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  // Session 4A — compare at the shelf (max 3, client-side, no extra Gemini call)
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  // Session 4C — did the user retake after a client-side quality reject?
  const hadQualityReject = useRef(false);

  // Session 2B — wrong-mode switch from the meal tab reuses the SAME photo.
  useEffect(() => {
    if (externalCapture) {
      onExternalConsumed?.();
      handleLabelCapture(externalCapture);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCapture]);

  const handleLabelCapture = async (dataUrl) => {
    if (labelBusy) return;
    if (!navigator.onLine) {
      onOffline?.();
      return;
    }
    // Client-side 6th-scan gate before we even fire the request.
    if (guest && !user && guestStore.scanCount >= 5) {
      trackGuest("signup_shown", { context: "6th_scan_gate", trigger: "label_client" });
      onRequireSignupGate?.();
      return;
    }
    // Session 2A — quality pre-check BEFORE spending a Gemini call. A
    // rejected image never reaches the server and never counts.
    setLabelNotice(null);
    const q = await checkImageQuality(dataUrl);
    if (!q.ok) {
      trackGuest("quality_reject", { reason: q.reason });
      fireScanEvent("quality_reject", { reason: q.reason });
      hadQualityReject.current = true;
      setLabelNotice(q.message);
      return;
    }
    if (hadQualityReject.current) {
      hadQualityReject.current = false;
      fireScanEvent("retake_after_reject", {});
    }
    setLabelBusy(true);
    setLabelError(null);
    setLabelResult(null);
    setLabelImage(dataUrl);
    try {
      const anon_id = guestStore.ensureAnonId();
      const b64 = dataUrl.includes(",") ? dataUrl.split(",", 2)[1] : dataUrl;
      const body = { image_base64: b64, anon_id };
      if (mergePrior) body.prior_canonical = mergePrior;
      const { data } = await apiClient.post("/nutrition/scan-label", body);
      if (data?.wrong_mode === "food") {
        trackGuest("wrong_mode_fired", { from: "packet", to: "food" });
        onWrongMode?.(dataUrl);
        return;
      }
      if (data?.error === "front_of_pack") {
        // Camera stays open, scan not counted.
        setLabelNotice(data.reason || "Turn the pack over and photograph the nutrition panel.");
        return;
      }
      if (data?.error) {
        setLabelError(
          data.error === "no_label_detected"
            ? "We couldn't see a nutrition label. Point at the panel on the back of the pack and try again."
            : data.error === "scan_failed"
              ? (data.reason || "We couldn't analyse this right now. Please try again in a moment.")
              : (data.reason || "Label wasn't clear enough. Try again."));
        return;
      }
      setLabelResult(data);
      setMergePrior(null);
      setMergeKind(null);
      // Compare flow: the next scan after "Compare with another" joins the
      // comparison instead of standing alone.
      if (compareList.length >= 1 && data?.canonical?.nutrition_per_100) {
        setCompareList((l) => [...l, data.canonical].slice(0, 3));
        setLabelResult(null);
        setShowCompare(true);
      }
      trackGuest("scan_completed", { food_identified: true, scan_type: "label_ocr" });
      const ingredientsOnly = data?.canonical?.result_state === "ingredients_only";
      if (guest && !ingredientsOnly) {
        const src = data.per_serving?.calories != null ? data.per_serving : (data.per_100g || {});
        guestStore.addScan({
          client_scan_id: `label_${Date.now()}`,
          dish_name: data.product_name || data.brand || "Packaged food",
          calories: src.calories || 0,
          protein_g: src.protein_g || 0,
          carbs_g: src.carbs_g || 0,
          fat_g: src.fat_g || 0,
          portion_multiplier: 1.0,
          scanned_at: new Date().toISOString(),
          thumbnail_base64: null,
          source: "label_ocr",
        });
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        const kind = err?.response?.data?.detail?.error;
        if (kind === "guest_cap_reached" || kind === "daily_budget_reached") {
          onRequireSignupGate?.();
        } else {
          onRequireReg?.("rate_limit");
        }
      } else {
        setLabelError("Could not read the label. Try again in a moment.");
      }
    } finally {
      setLabelBusy(false);
    }
  };

  const resetLabel = () => {
    setLabelResult(null);
    setLabelError(null);
    setLabelImage(null);
    setLabelNotice(null);
  };

  // Session 2C — second photo merges into the kept canonical record.
  const startAddPhoto = (kind) => {
    if (labelResult?.canonical) {
      setMergePrior(labelResult.canonical);
      setMergeKind(kind);
      trackGuest("multi_photo_started", { kind });
    }
    resetLabel();
  };

  return (
    <div data-testid="packet-tab">
      {showCompare && compareList.length >= 2 ? (
        <CompareView
          products={compareList}
          onAddAnother={() => {
            setShowCompare(false);
            setLabelNotice("Scan the next product to compare — uses 1 scan.");
          }}
          onClose={() => {
            setCompareList([]);
            setShowCompare(false);
            resetLabel();
          }}
        />
      ) : labelBusy ? (
        <div className="bg-white rounded-[16px] p-5 border border-line2 text-center" data-testid="label-busy">
          {labelImage ? (
            <img src={labelImage} alt="" className="w-full mb-3 rounded-[12px]"
                 style={{ height: 180, objectFit: "cover" }} />
          ) : null}
          <div className="w-8 h-8 mx-auto rounded-full border-2 border-black/10 border-t-black animate-spin" />
          <div className="mt-3 font-display text-[16px]">Reading the label…</div>
          <p className="text-[12px] text-ink-soft mt-0.5">Usually ~6 seconds.</p>
        </div>
      ) : labelResult ? (
        <LabelResults
          label={labelResult}
          scannedImage={labelImage}
          onDiscard={resetLabel}
          onAddPhoto={startAddPhoto}
          onCompare={() => {
            if (labelResult?.canonical) {
              setCompareList([labelResult.canonical]);
              resetLabel();
              setLabelNotice("Scan the next product to compare — uses 1 scan.");
            }
          }}
          onSaved={() => { refreshRecent?.(); setTimeout(resetLabel, 400); }}
        />
      ) : labelError ? (
        <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="label-error">
          <div className="font-display text-[18px]">Try again</div>
          <p className="text-[13px] mt-1" style={{ color: "#4A4845" }}>{labelError}</p>
          <button
            type="button"
            onClick={resetLabel}
            data-testid="label-error-retake"
            className="mt-3 w-full h-11 rounded-pill"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
          >
            Retake photo
          </button>
        </div>
      ) : !manualMode ? (
        <>
          {mergePrior && (
            <div
              className="mb-2 rounded-[12px] p-3 text-[12px] flex items-center justify-between gap-2"
              data-testid="label-merge-notice"
              style={{ background: "rgba(0,212,255,0.12)", border: "1px solid rgba(0,212,255,0.3)" }}
            >
              <span>
                Photograph the {mergeKind === "panel" ? "nutrition panel" : "ingredients list"} —
                this second photo uses <b>1 scan</b>.
              </span>
              <button
                type="button"
                data-testid="label-merge-cancel"
                onClick={() => { setMergePrior(null); setMergeKind(null); }}
                className="text-[12px] font-semibold underline shrink-0"
              >
                Cancel
              </button>
            </div>
          )}
          {labelNotice && (
            <div
              className="mb-2 rounded-[12px] p-3 text-[13px]"
              data-testid="label-quality-notice"
              style={{ background: "rgba(42,40,37,0.08)", border: "1px solid rgba(42,40,37,0.2)", color: "#1A1A1A" }}
            >
              {labelNotice}
            </div>
          )}
          <BarcodeScanner
            active={active && !manualMode && !labelBusy && !labelResult}
            labelMode={labelMode}
            onDetected={(digits) => {
              setBarcodeInput(digits);
              onBarcode?.(digits);
            }}
            onCapture={handleLabelCapture}
            onManualClick={() => setManualMode(true)}
          />
          <p className="mt-2 text-[11px] text-ink-soft text-center">
            {labelMode
              ? "Point at the nutrition panel and tap the shutter"
              : "Aim at a barcode, or tap the shutter to read the nutrition label"}
          </p>
          {/* Session 4D — privacy, plainly stated, not buried */}
          <p className="mt-1 text-[10px] text-center" data-testid="scanner-privacy-line"
             style={{ color: "rgba(42,40,37,0.5)" }}>
            Label photos are analysed by Google Gemini and aren't stored by FitFrik.{" "}
            <Link to="/legal/privacy" className="underline">Privacy</Link>
            {" · "}
            <Link to="/nutrition/saved" className="underline" data-testid="saved-products-link">
              Saved products
            </Link>
          </p>
        </>
      ) : (
        <div className="bg-white rounded-[16px] p-5 border border-line2">
          <h3 className="font-display text-[18px]">Enter barcode digits</h3>
          <p className="text-[12px] text-ink-soft mt-1">
            Read the 8–14 digit number under the barcode on the pack.
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ""))}
            placeholder="e.g. 8901030641215"
            data-testid="barcode-input"
            className="mt-3 w-full h-12 px-3 rounded-[12px] bg-canvas2 border border-line outline-none text-[14px]"
          />
          <button
            onClick={() => barcodeInput && onBarcode?.(barcodeInput)}
            disabled={barcodeBusy}
            data-testid="barcode-lookup"
            className="mt-3 w-full h-12 rounded-pill disabled:opacity-50"
            style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100, fontWeight: 600 }}
          >
            {barcodeBusy ? "Looking up…" : "Look up product"}
          </button>
          <button
            type="button"
            onClick={() => setManualMode(false)}
            data-testid="barcode-use-camera"
            className="mt-2 w-full h-11 text-[13px] font-medium"
            style={{ color: "#4A4845" }}
          >
            Use camera instead
          </button>
        </div>
      )}
    </div>
  );
}
